import { useCallback, useEffect, useState } from "react";
import { api } from "./api/client";
import { GuestHomeStylePortal } from "./components/GuestHomeStylePortal";
import {
  guestGoogleConfigured,
  developmentGuestProviderConfig,
  kakaoAddressConfigured,
  mountKakaoStaticMap,
  requestGuestGoogleCredential,
  selectKakaoAddress,
} from "./guestProviders";

export interface GuestRoute {
  key: string;
  inviteToken?: string;
  projectId?: string;
  view?: "projects" | "project" | "intake";
}

const GUEST_PORTAL_HOSTNAMES = new Set([
  "guest.projectselmo.kr",
  "guest.genesishjh.cloud",
]);

export function guestPortalHostname(hostname: string): string | undefined {
  const normalized = hostname.toLowerCase().replace(/\.$/, "");
  return GUEST_PORTAL_HOSTNAMES.has(normalized) ? normalized : undefined;
}

export function isGuestPortalEntry(hostname: string, pathname: string): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  const localDevelopmentHost = host === "localhost" || host === "127.0.0.1" || host === "::1";
  return guestPortalHostname(host) !== undefined
    || (localDevelopmentHost && /^\/guest(?:\/|$)/.test(pathname));
}

function safeDecode(value: string): string | undefined {
  try { return decodeURIComponent(value); } catch { return undefined; }
}

export function guestRouteFromLocation(pathname: string, search: string, hash = ""): GuestRoute {
  const normalized = pathname.replace(/^\/guest(?=\/|$)/, "") || "/";
  // Invite bearer tokens are fragment-only. Query and path fallbacks would be
  // sent to reverse-proxy access logs before JavaScript could clean the URL.
  const fragmentJoin = hash.match(/^#\/?join\/([^/]+)\/?$/);
  const project = normalized.match(/^\/projects\/([^/]+)(\/intake)?\/?$/);
  const projectList = /^\/projects\/?$/.test(normalized);
  const inviteToken = fragmentJoin ? safeDecode(fragmentJoin[1]) : undefined;
  const projectId = project ? safeDecode(project[1]) : undefined;
  return {
    key: `${pathname}${search}${hash}`,
    ...(inviteToken ? { inviteToken } : {}),
    ...(project && projectId ? {
      projectId,
      view: project[2] ? "intake" as const : "project" as const,
    } : {}),
    ...(projectList ? { view: "projects" as const } : {}),
  };
}

function readRoute(): GuestRoute {
  return guestRouteFromLocation(window.location.pathname, window.location.search, window.location.hash);
}

export default function GuestApp() {
  const [route, setRoute] = useState(readRoute);
  const [providerConfig, setProviderConfig] = useState(developmentGuestProviderConfig);
  const [configResolved, setConfigResolved] = useState(false);
  const [portalEnabled, setPortalEnabled] = useState<boolean>();
  const basePath = window.location.pathname.startsWith("/guest") ? "/guest" : "";

  useEffect(() => {
    const onPopState = () => setRoute(readRoute());
    window.addEventListener("popstate", onPopState);
    window.addEventListener("hashchange", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("hashchange", onPopState);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void api.guestConfig(controller.signal).then((config) => {
      setPortalEnabled(config.enabled);
      setProviderConfig(config.enabled ? {
        googleClientId: config.googleClientId ?? undefined,
        kakaoJavascriptKey: config.kakaoJavascriptKey ?? undefined,
      } : {});
      setConfigResolved(true);
    }).catch(() => {
      // A missing runtime config is indistinguishable from a disabled or
      // unreachable portal in production, so never expose a usable login UI.
      // Local development may keep its explicit VITE provider fallback.
      setPortalEnabled(import.meta.env.DEV);
      if (!import.meta.env.DEV) setProviderConfig({});
      setConfigResolved(true);
    });
    return () => controller.abort();
  }, []);

  const navigate = useCallback((path: string) => {
    const next = `${basePath}${path === "/" ? "" : path}` || "/";
    window.history.pushState(window.history.state, "", next);
    setRoute(readRoute());
  }, [basePath]);
  const cleanInviteUrl = useCallback(() => {
    window.history.replaceState(window.history.state, "", `${basePath}/onboarding` || "/onboarding");
  }, [basePath]);

  if (!configResolved) {
    return <main className="guest-home-style guest-hs-unavailable" aria-busy="true"><section><small>PROJECT SELMO</small><h1>고객 포털 확인 중…</h1><p>로그인과 주소 검색 설정을 안전하게 확인하고 있습니다.</p></section></main>;
  }

  if (portalEnabled === false) {
    return <main className="guest-home-style guest-hs-unavailable"><section><small>PROJECT SELMO</small><h1>고객 포털 점검 중</h1><p>초대와 로그인이 일시 중지되었습니다. 운영자에게 문의해 주세요.</p></section></main>;
  }

  return <GuestHomeStylePortal
    portalHostname={guestPortalHostname(window.location.hostname) ?? "guest.projectselmo.kr"}
    inviteToken={route.inviteToken}
    initialProjectId={route.projectId}
    initialView={route.view}
    navigate={navigate}
    cleanInviteUrl={cleanInviteUrl}
    requestGoogleCredential={configResolved && guestGoogleConfigured(providerConfig.googleClientId) ? () => requestGuestGoogleCredential(providerConfig.googleClientId) : undefined}
    selectKakaoAddress={configResolved && kakaoAddressConfigured(providerConfig.kakaoJavascriptKey) ? () => selectKakaoAddress(providerConfig.kakaoJavascriptKey) : undefined}
    mountKakaoMap={configResolved && kakaoAddressConfigured(providerConfig.kakaoJavascriptKey) ? (container, selection) => mountKakaoStaticMap(providerConfig.kakaoJavascriptKey, container, selection) : undefined}
  />;
}
